import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Loader2, Rocket } from 'lucide-react';
import axios from 'axios';
import SEO from '@/components/SEO';
import AccordionItem from '@/components/ds/AccordionItem';
import {
  HERO, WHAT_YOU_GET, QUESTIONS, HOW_IT_WILL_WORK, INSIDE_REPORT, PREVIEW, FAQS, CLOSING,
} from '@/pages/mvpValidatorContent';
import '@/styles/brand.css';
import '@/styles/forms.css';
import '@/styles/landing.css';
import '@/styles/mvp.css';

/**
 * /mvp-validator — rebuilt against the "MVP Validator (Coming Soon)" artboard.
 *
 * ── WHAT WAS ALREADY HERE, AND IS STILL HERE ────────────────────────────────
 *
 * This page EXISTED and was already a coming-soon page with a working waitlist.
 * So this is a redesign of a live page, not a new build, and the content rules
 * apply: the h1, the lead, the "Everything you need to validate" heading, its
 * four cards, the closing heading and the /ux-auditor link are all carried
 * across unchanged. The SEO block and the waitlist handler are byte-for-byte
 * what they were, including the endpoint and both of its failure messages.
 *
 * The artboard's sections are ADDITIONS around that spine.
 *
 * OVERLAP, FLAGGED NOT RESOLVED: the artboard's "What the Report Will Answer"
 * covers similar ground to the existing "Everything you need to validate".
 * Both are kept, because deleting one would be a content change made to tidy a
 * redundancy rather than because anything was wrong with it. Worth a look.
 *
 * ── THE OMISSION ────────────────────────────────────────────────────────────
 *
 * The artboard's logo wall — five named companies "already using UXAuditPro" —
 * is not built. See mvpValidatorContent.js for why: it asserts customers that
 * do not exist, and it contradicts the four other places on this same page that
 * say the product is not finished.
 *
 * ── FONTS ───────────────────────────────────────────────────────────────────
 *
 * The artboard specifies Instrument Sans and Bricolage Grotesque. The shipped
 * system is Host Grotesk and Anton, self-hosted and guarded by
 * critical-font-stack.test.js. The system wins, on the same reasoning as the
 * artboard's 44px h2 losing to the scale's 38px: the artboard is ground truth
 * for STRUCTURE, the shipped system for TYPE. Flagged to the owner.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MvpValidatorPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState('');
  const [faq, setFaq] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/auth/mvp-waitlist`, { email });
      setMessage(resp.data.message || "You're on the list!");
      setSubmitted(true);
    } catch (err) {
      setMessage("Thanks! We'll notify you at launch.");
      setSubmitted(true);
    }
    setLoading(false);
  };

  /* `align` is per call site, deliberately. The hero sits in a left-aligned
     column (h1, sub and note all textAlign left), so a centred control under
     left copy reads as a mistake; the closing band is a centred composition and
     wants centre. One form, two grounds, so alignment is an argument rather
     than a value baked into the shared markup. */
  const waitlistForm = (idSuffix, align = 'center') => (
    submitted ? (
      <p className="uxa-lead" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, color: 'var(--success-text)', margin: 0 }}>
        <Check className="w-5 h-5" aria-hidden="true" />
        {message}
      </p>
    ) : (
      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: align }}>
        <label className="uxa-visually-hidden" htmlFor={`mvp-email-${idSuffix}`}>Email address</label>
        <input
          id={`mvp-email-${idSuffix}`}
          className="uxa-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
          style={{ flex: '1 1 240px', minWidth: 0, maxWidth: 300 }}
        />
        <button type="submit" className="uxa-submit uxa-submit--auto" disabled={loading} aria-busy={loading}>
          {loading ? 'Joining…' : 'Join the Waitlist'}
          <span className="uxa-submit-knob" aria-hidden="true">
            {loading ? <Loader2 className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
          </span>
        </button>
      </form>
    )
  );

  return (
    <div>
      <SEO
        title="AI MVP Validator: Validate Your Startup Idea"
        description="Coming soon: AI-powered MVP validation tool. Get a GO, PIVOT, or NO-GO verdict on your startup idea with market sizing, competitor analysis & customer evidence."
        canonical="/mvp-validator"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'AI MVP Validator by UXAuditPro',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          url: 'https://www.uxauditpro.com/mvp-validator/',
          description: 'AI-powered MVP validation tool that analyses market demand, competitors, and customer evidence to deliver a GO/PIVOT/NO-GO verdict.',
          brand: { '@type': 'Brand', name: 'UXAuditPro' },
        }}
      />

      {/* ═══ HERO ══════════════════════════════════════════════════════════════
          TWO COLUMNS, 46/54, per the artboard. The report card belongs IN the
          hero; the first build pulled it out into a section of its own and
          centred the left column, which is what made this page look nothing
          like the design it was built from. */}
      <header className="uxa-mvp-hero">
        <div className="uxa-mvp-hero__hatch" aria-hidden="true" />
        <div className="uxa-mvp-hero__grid">
          <div style={{ paddingTop: 20 }}>
            <p className="uxa-mvp-badge">
              <span className="uxa-mvp-badge__dot" aria-hidden="true" />
              {HERO.badge}
              <span style={{ color: 'var(--ink-2)', fontWeight: 500 }}>· {HERO.badgeNote}</span>
            </p>
            <h1 className="uxa-h1" style={{ color: 'var(--ink-1)', textAlign: 'left', maxWidth: 520, margin: '22px 0 0' }}>
              {HERO.h1}
            </h1>
            <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ink-2)', margin: '26px 0 0', maxWidth: 420, textWrap: 'pretty' }}>
              {HERO.sub}
            </p>
            <div style={{ marginTop: 36, maxWidth: 440 }}>{waitlistForm('hero', 'flex-start')}</div>
            <p style={{ fontSize: 'var(--type-dense-size)', color: 'var(--ink-2)', marginTop: 12, minHeight: 19 }}>
              {submitted ? '' : HERO.note}
            </p>
            <div className="uxa-mvp-stats">
              {HERO.stats.map((st) => (
                <div key={st.label}>
                  <div className="uxa-mvp-stats__value">{st.value}</div>
                  <p className="uxa-mvp-stats__label">{st.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* The design preview. The chip is part of the component, not a
              caption someone added: it is what separates a picture of the
              report FORMAT from a claim that this validation was run. */}
          <div className="uxa-mvp-shot">
            <div className="uxa-mvp-shot__bloom" aria-hidden="true" />
            <div className="uxa-mvp-card">
              <div className="uxa-mvp-card__head">
                <span aria-hidden="true" style={{ width: '22px', height: '22px', borderRadius: 7, background: 'var(--accent)', color: 'var(--on-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'var(--type-weight-mark)', fontSize: 9 }}>UX</span>
                <span style={{ fontWeight: 700, fontSize: 'var(--type-body-size)', letterSpacing: '-0.02em' }}>Validation Report</span>
                <span style={{ fontSize: 'var(--type-dense-size)', color: 'var(--ink-2)' }}>· {PREVIEW.idea}</span>
                <span style={{ marginLeft: 'auto', padding: '5px 12px', borderRadius: 'var(--radius-pill)', background: 'var(--surface-sand-deep)', fontSize: 'var(--type-dense-size)', fontWeight: 'var(--type-weight-ui)', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ink-2)' }}>
                  {PREVIEW.label}
                </span>
              </div>

              <div className="uxa-mvp-card__grid">
                <div className="uxa-mvp-panel">
                  <p className="uxa-mvp-label">Verdict</p>
                  <div className="uxa-mvp-verdict">{PREVIEW.verdict}</div>
                  <p style={{ fontSize: 'var(--type-dense-size)', lineHeight: 1.6, color: 'var(--ink-2)', margin: '14px 0 0' }}>{PREVIEW.verdictNote}</p>
                  <hr className="uxa-report-rule" style={{ margin: '18px 0' }} />
                  <p style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: 0 }}>
                    <span style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.03em' }}>{PREVIEW.score}/100</span>
                    <span style={{ fontSize: 'var(--type-dense-size)', color: 'var(--ink-2)' }}>validation score</span>
                  </p>
                  <span className="uxa-mvp-dim__track" style={{ display: 'block', marginTop: 12, height: 6 }} aria-hidden="true">
                    <span style={{ width: `${PREVIEW.score}%`, background: 'var(--accent)' }} />
                  </span>
                </div>

                <div className="uxa-mvp-panel">
                  <p style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Dimension Findings</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 18 }}>
                    {PREVIEW.dimensions.map((d) => (
                      <span key={d.name} className="uxa-mvp-dim">
                        <span className="uxa-mvp-dim__name">{d.name}</span>
                        <span className="uxa-mvp-dim__track" aria-hidden="true">
                          <span style={{ width: `${d.value}%`, background: d.strong ? 'var(--accent)' : 'var(--accent-soft)' }} />
                        </span>
                        <span className="uxa-mvp-dim__value">{d.value}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="uxa-mvp-metrics">
                {PREVIEW.metrics.map((m) => (
                  <div key={m.label}>
                    <p className="uxa-mvp-stats__label" style={{ margin: 0 }}>{m.label}</p>
                    <div style={{ fontSize: 'var(--type-h3-size)', fontWeight: 700, margin: '2px 0 0' }}>{m.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ═══ WHAT THE REPORT WILL ANSWER ══════════════════════════════════════
          Four white cards on the LIGHT ground. The first build made this a dark
          panel, which is the artboard's tonal break for a different section. */}
      <section className="uxa-landing-section uxa-pt-xl">
        <div style={{ textAlign: 'center' }}>
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>{QUESTIONS.heading}</h2>
          <p style={{ fontSize: 'var(--type-dense-size)', lineHeight: 'var(--type-dense-leading)', color: 'var(--ink-2)', margin: '18px auto 0', maxWidth: 470, textWrap: 'pretty' }}>
            {QUESTIONS.lead}
          </p>
        </div>
        <div className="uxa-mvp-grid-4">
          {QUESTIONS.items.map((q, i) => (
            <div key={q.q} className="uxa-mvp-tile">
              {/* Four marks, four shapes — square, circle, diamond, ring — as
                  the artboard draws them. Decorative and aria-hidden: the h3
                  carries the meaning, so nothing is lost if they do not load. */}
              <span className="uxa-mvp-tile__mark" aria-hidden="true">
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: ['4px', '50%', '3px', '50%'][i],
                    transform: i === 2 ? 'rotate(45deg)' : undefined,
                    background: i === 3 ? 'transparent' : 'var(--accent)',
                    border: i === 3 ? '2px solid var(--accent)' : undefined,
                  }}
                />
              </span>
              <h3 className="uxa-h3" style={{ margin: '20px 0 8px' }}>{q.q}</h3>
              <p style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--ink-2)', margin: 0 }}>{q.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ HOW IT WILL WORK ══════════════════════════════════════════════════
          Inside a sand panel at radius 34 with a 58px grid — the light
          counterpart of the homepage's dark band. */}
      <section className="uxa-landing-section uxa-pt-xl">
        <div className="uxa-mvp-panel-sand">
          <div className="uxa-mvp-panel-sand__grid" aria-hidden="true" />
          <div style={{ textAlign: 'center' }}>
            <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>{HOW_IT_WILL_WORK.heading}</h2>
            <p style={{ fontSize: 'var(--type-dense-size)', lineHeight: 'var(--type-dense-leading)', color: 'var(--ink-2)', margin: '18px auto 0', maxWidth: 440, textWrap: 'pretty' }}>
              {HOW_IT_WILL_WORK.lead}
            </p>
          </div>
          <div className="uxa-mvp-grid-3">
            {HOW_IT_WILL_WORK.steps.map((st, i) => (
              <div key={st.title} className="uxa-mvp-tile">
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="uxa-mvp-tile__step">{i + 1}</span>
                  <span style={{ fontSize: 'var(--type-dense-size)', fontWeight: 'var(--type-weight-ui)', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-2)' }}>{st.when}</span>
                </span>
                <h3 className="uxa-h3" style={{ margin: '20px 0 8px' }}>{st.title}</h3>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--ink-2)', margin: 0 }}>{st.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ INSIDE THE REPORT ═════════════════════════════════════════════════
          0.85 / 1.15, page list on the left, a second design preview on the
          right — the executive-verdict page of the same example report. */}
      <section className="uxa-landing-section uxa-pt-xl">
        <div className="uxa-split" style={{ alignItems: 'center' }}>
          <div>
            <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>{INSIDE_REPORT.heading}</h2>
            <p style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--ink-2)', margin: '18px 0 0', maxWidth: 340, textWrap: 'pretty' }}>
              {INSIDE_REPORT.lead}
            </p>
            <ul className="uxa-mvp-toc" style={{ listStyle: 'none', padding: 0 }}>
              {INSIDE_REPORT.rows.map((r) => (
                <li key={r.title}>
                  <span style={{ fontWeight: 500 }}>{r.title}</span>
                  <span style={{ color: 'var(--ink-2)' }}>{r.pages}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="uxa-mvp-card" style={{ boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-1)' }}>
            <div className="uxa-mvp-card__head">
              <span className="uxa-mvp-label">Executive verdict</span>
              <span style={{ marginLeft: 'auto' }} className="uxa-mvp-verdict">{PREVIEW.verdict}</span>
            </div>
            <h3 className="uxa-h3" style={{ margin: '18px 0 0' }}>{PREVIEW.idea}</h3>
            <p style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--ink-2)', margin: '12px 0 0' }}>{PREVIEW.summary}</p>
            <div className="uxa-grid-2" style={{ gap: 14, marginTop: 18 }}>
              {PREVIEW.signals.map((sig) => (
                <div key={sig.label} style={{ background: 'var(--surface-sand)', borderRadius: 'var(--radius-md)', padding: 16 }}>
                  <p className="uxa-mvp-label">{sig.label}</p>
                  <p style={{ fontSize: 'var(--type-dense-size)', lineHeight: 1.6, margin: '8px 0 0' }}>{sig.body}</p>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap', alignItems: 'center' }}>
              {PREVIEW.footnotes.map((fn, i) => (
                <span key={fn} style={{ fontSize: 'var(--type-dense-size)', fontWeight: 500, color: 'var(--ink-2)', padding: '5px 12px', borderRadius: 'var(--radius-pill)', background: i === 2 ? 'transparent' : 'var(--surface-sand-deep)', marginLeft: i === 2 ? 'auto' : 0 }}>
                  {fn}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═══════════════════════════════════════════════════════════════ */}
      <section className="uxa-landing-section uxa-pt-xl">
        <div className="uxa-split">
          <div>
            <span className="section-label">FAQ</span>
            <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px 0 0' }}>Frequently Asked Questions</h2>
            <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '16px 0 0' }}>
              Everything founders ask before sending an idea in.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {FAQS.map((f, i) => (
              <AccordionItem
                key={f.q}
                question={<h3 className="uxa-disclosure-title" style={{ margin: 0 }}>{f.q}</h3>}
                open={faq === i}
                onToggle={() => setFaq(faq === i ? -1 : i)}
              >
                <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 0 }}>{f.a}</p>
              </AccordionItem>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CLOSING ═══════════════════════════════════════════════════════════ */}
      <section className="uxa-closing" style={{ marginTop: 120 }}>
        <div className="uxa-closing__grid" aria-hidden="true" />
        <div style={{ position: 'relative', maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>{CLOSING.heading}</h2>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '18px auto 0', maxWidth: 520 }}>{CLOSING.lead}</p>
          <div style={{ marginTop: 40 }}>{waitlistForm('closing')}</div>
          <p style={{ marginTop: 32 }}>
            <Link to="/ux-auditor/" className="uxa-submit uxa-submit--auto uxa-submit--tertiary">
              Try the UX Auditor while you wait
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
}
